import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type ChecklistStatus =
  | "NotStarted"
  | "InProgress"
  | "Completed"
  | "Skipped"
  | "Snoozed";

export type SetupStage = "Intake" | "Checklist";

export type IntakeQuestionKind =
  | "YesNo"
  | "FilingStatus"
  | "State"
  | "Choice"
  | "Number"
  | "Date"
  | "EntityType";

export interface IntakeQuestionOption {
  value: string;
  label: string;
  description?: string | null;
}

export interface IntakeQuestion {
  key: string;
  prompt: string;
  helpText: string;
  kind: IntakeQuestionKind;
  options?: IntakeQuestionOption[] | null;
  isRequired: boolean;
}

/**
 * A single planner-generated checklist item, merged with any persisted user
 * state (Completed / Skipped / Snoozed). `shownBecause` is the human-readable
 * reason the planner decided THIS item applies to THIS user — never generic.
 */
export interface SetupChecklistItem {
  id: string;
  key: string;
  title: string;
  whyItMatters: string;
  shownBecause: string;
  routeOnClick?: string | null;
  routeLabel?: string | null;
  authorityUrl?: string | null;
  sortOrder: number;
  status: ChecklistStatus;
  completedAt?: string | null;
  snoozedUntil?: string | null;
  isHighPenalty: boolean;
  isAutoTracked: boolean;
  estimatedRiskLabel?: string | null;
}

export interface SetupChecklistResponse {
  /**
   * "Intake" → we don't know enough yet; UI should render a short intake form
   * using `intake`. "Checklist" → UI renders the derived list from `items`.
   */
  stage: SetupStage;
  intake: IntakeQuestion[];
  items: SetupChecklistItem[];
  progress: { completed: number; total: number };
}

const KEY = ["setup-checklist"];

export function useSetupChecklist() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<SetupChecklistResponse> => {
      const r = await api().get<SetupChecklistResponse>("/api/setup");
      return r.data;
    },
  });
}

export function useCompleteChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      const r = await api().post<SetupChecklistItem>(
        `/api/setup/${encodeURIComponent(key)}/complete`,
      );
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSkipChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      const r = await api().post<SetupChecklistItem>(
        `/api/setup/${encodeURIComponent(key)}/skip`,
      );
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useResetChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      const r = await api().post<SetupChecklistItem>(
        `/api/setup/${encodeURIComponent(key)}/reset`,
      );
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSnoozeChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; days?: number }) => {
      const r = await api().post<SetupChecklistItem>(
        `/api/setup/${encodeURIComponent(input.key)}/snooze?days=${input.days ?? 7}`,
      );
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Stamps FamilyProfile.OnboardingCompletedAt on the backend. After this
 * resolves, the planner stops returning stage="Intake" on /api/setup so the
 * wizard route guard releases the user into the rest of the app. Invalidates
 * both the setup checklist and family-profile queries so everything downstream
 * sees the new state.
 *
 * CRITICAL — the family-profile query key must exactly match the one in
 * useProfile.ts (["profile", "family"]). A mismatch means the AppShell guard
 * never re-reads the family profile after the stamp, the overlay stays
 * mounted, and the user is trapped inside a "finished" wizard forever.
 */
export function useCompleteIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api().post<{ onboardingCompletedAt: string }>(
        "/api/setup/intake-complete",
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["profile", "family"] });
      qc.invalidateQueries({ queryKey: ["profile", "business"] });
      qc.invalidateQueries({ queryKey: ["advisor", "cards"] });
    },
  });
}
